// Markdown ↔ Tiptap doc round-trip using prosemirror-markdown.
//
// The WS wire format stays markdown (zero backend change). Tiptap holds an
// in-memory ProseMirror doc; we serialize on every keystroke for the outbound
// debounce, and parse incoming snapshots into a doc for setContent.
//
// Custom block-level sentinels (server is source of truth — see
// `internal/api/ws/snippets/snippets.go`):
//   [[hidden id=<uuid> visible=editor,viewer]]\n<body>\n[[/hidden]]
//   [[hidden id=<uuid> redacted]]
// Parsed into hiddenBlock / hiddenPlaceholder Tiptap nodes and serialized
// back verbatim.

import MarkdownIt from 'markdown-it';
import {
  MarkdownParser,
  MarkdownSerializer,
  defaultMarkdownSerializer,
} from 'prosemirror-markdown';

const HIDDEN_OPEN = /^\[\[hidden\s+id=([0-9a-f-]{36})\s+visible=([a-z,]*)\]\]\s*$/i;
const HIDDEN_CLOSE = /^\[\[\/hidden\]\]\s*$/;
const HIDDEN_REDACTED = /^\[\[hidden\s+id=([0-9a-f-]{36})\s+redacted\]\]\s*$/i;

// Inline rule: `<u>...</u>` opens/closes an underline mark. Markdown has no
// native underline syntax; the HTML sentinel is small, round-trips cleanly,
// and is ignored by every renderer that strips raw HTML.
function underlinePlugin(md) {
  md.inline.ruler.after('emphasis', 'underline_inline', (state, silent) => {
    const start = state.pos;
    if (state.src.slice(start, start + 3) !== '<u>') return false;
    const end = state.src.indexOf('</u>', start + 3);
    if (end < 0) return false;
    if (silent) return true;

    state.push('u_open', 'u', 1);
    const oldPos = state.pos;
    const oldMax = state.posMax;
    state.pos = start + 3;
    state.posMax = end;
    state.md.inline.tokenize(state);
    state.pos = oldPos;
    state.posMax = oldMax;
    state.push('u_close', 'u', -1);
    state.pos = end + 4;
    return true;
  });
}

// Inline rule: `<char>NAME</char>` (optionally `<char init="chapterId">NAME</char>`)
// becomes a `char` mark on the inner text. Mirrors the underline pattern.
// The wrapper never lives as literal text in the ProseMirror doc, so the
// caret can't accidentally land inside it after insertion (the bug we hit
// with raw-text injection). The optional `init` attribute round-trips so
// the decoupage pipeline can identify a character's first appearance.
function charPlugin(md) {
  md.inline.ruler.after('emphasis', 'char_inline', (state, silent) => {
    const start = state.pos;
    if (state.src.charCodeAt(start) !== 0x3c /* '<' */) return false;
    // Match `<char` then either `>` or whitespace before attrs + `>`.
    const head = state.src.slice(start, start + 5);
    if (head !== '<char') return false;
    let openEnd = -1;
    let init = null;
    if (state.src.charCodeAt(start + 5) === 0x3e /* '>' */) {
      openEnd = start + 6;
    } else if (/\s/.test(state.src.charAt(start + 5))) {
      const gt = state.src.indexOf('>', start + 5);
      if (gt < 0) return false;
      const attrs = state.src.slice(start + 5, gt);
      const initMatch = /\binit\s*=\s*"([^"]*)"/.exec(attrs);
      if (initMatch) init = initMatch[1];
      openEnd = gt + 1;
    } else {
      return false;
    }
    const end = state.src.indexOf('</char>', openEnd);
    if (end < 0) return false;
    if (silent) return true;

    const openTok = state.push('char_open', 'char', 1);
    if (init) openTok.attrs = [['init', init]];
    const oldPos = state.pos;
    const oldMax = state.posMax;
    state.pos = openEnd;
    state.posMax = end;
    state.md.inline.tokenize(state);
    state.pos = oldPos;
    state.posMax = oldMax;
    state.push('char_close', 'char', -1);
    state.pos = end + 7;
    return true;
  });
}

// markdown-it block rule. Walks lines and turns the bracketed sentinels into
// dedicated tokens the ProseMirror parser maps to nodes.
function hiddenBlockPlugin(md) {
  md.block.ruler.before(
    'fence',
    'hidden_block',
    (state, startLine, endLine, silent) => {
      const start = state.bMarks[startLine] + state.tShift[startLine];
      const max = state.eMarks[startLine];
      const line = state.src.slice(start, max);

      const redactedM = HIDDEN_REDACTED.exec(line);
      if (redactedM) {
        if (silent) return true;
        const tok = state.push('hidden_placeholder', '', 0);
        tok.attrs = [['id', redactedM[1]]];
        tok.map = [startLine, startLine + 1];
        state.line = startLine + 1;
        return true;
      }

      const openM = HIDDEN_OPEN.exec(line);
      if (!openM) return false;

      let nextLine = startLine + 1;
      let found = false;
      while (nextLine < endLine) {
        const s = state.bMarks[nextLine] + state.tShift[nextLine];
        const e = state.eMarks[nextLine];
        if (HIDDEN_CLOSE.test(state.src.slice(s, e))) {
          found = true;
          break;
        }
        nextLine++;
      }
      if (!found) return false;
      if (silent) return true;

      const id = openM[1];
      const visible = openM[2];

      const openTok = state.push('hidden_block_open', 'div', 1);
      openTok.attrs = [
        ['id', id],
        ['visible', visible],
      ];
      openTok.block = true;
      openTok.map = [startLine, nextLine + 1];

      // Re-tokenize the body as a nested block scope.
      const prevLineMax = state.lineMax;
      state.lineMax = nextLine;
      state.md.block.tokenize(state, startLine + 1, nextLine);
      state.lineMax = prevLineMax;

      const closeTok = state.push('hidden_block_close', 'div', -1);
      closeTok.block = true;

      state.line = nextLine + 1;
      return true;
    },
    { alt: [] },
  );
}

function tokenSpecs() {
  return {
    blockquote: { block: 'blockquote' },
    paragraph: { block: 'paragraph' },
    list_item: { block: 'listItem' },
    bullet_list: { block: 'bulletList' },
    ordered_list: {
      block: 'orderedList',
      getAttrs: (tok) => ({ start: +tok.attrGet('start') || 1 }),
    },
    heading: {
      block: 'heading',
      getAttrs: (tok) => ({ level: +tok.tag.slice(1) }),
    },
    code_block: { block: 'codeBlock', noCloseToken: true },
    fence: {
      block: 'codeBlock',
      getAttrs: (tok) => ({ language: tok.info || null }),
      noCloseToken: true,
    },
    hr: { node: 'horizontalRule' },
    hardbreak: { node: 'hardBreak' },
    em: { mark: 'italic' },
    strong: { mark: 'bold' },
    link: {
      mark: 'link',
      getAttrs: (tok) => ({
        href: tok.attrGet('href'),
        title: tok.attrGet('title') || null,
      }),
    },
    code_inline: { mark: 'code', noCloseToken: true },
    s: { mark: 'strike' },
    u: { mark: 'underline' },
    char: {
      mark: 'char',
      getAttrs: (tok) => ({ init: tok.attrGet('init') || null }),
    },
    hidden_block: {
      block: 'hiddenBlock',
      getAttrs: (tok) => ({
        id: tok.attrGet('id'),
        visible: (tok.attrGet('visible') || '').split(',').filter(Boolean),
      }),
    },
    hidden_placeholder: {
      node: 'hiddenPlaceholder',
      getAttrs: (tok) => ({ id: tok.attrGet('id') }),
    },
  };
}

const md = MarkdownIt('default', { html: false, linkify: true, breaks: false });
md.use(hiddenBlockPlugin);
md.use(underlinePlugin);
md.use(charPlugin);

let cachedParser = null;
let cachedSchemaRef = null;

export function parseMarkdown(schema, source) {
  if (cachedSchemaRef !== schema) {
    cachedParser = new MarkdownParser(schema, md, tokenSpecs());
    cachedSchemaRef = schema;
  }
  return cachedParser.parse(source || '');
}

export const tiptapMarkdownSerializer = new MarkdownSerializer(
  {
    blockquote: defaultMarkdownSerializer.nodes.blockquote,
    paragraph: defaultMarkdownSerializer.nodes.paragraph,
    listItem: defaultMarkdownSerializer.nodes.list_item,
    bulletList: defaultMarkdownSerializer.nodes.bullet_list,
    orderedList: defaultMarkdownSerializer.nodes.ordered_list,
    heading: defaultMarkdownSerializer.nodes.heading,
    horizontalRule: defaultMarkdownSerializer.nodes.horizontal_rule,
    hardBreak: defaultMarkdownSerializer.nodes.hard_break,
    text: defaultMarkdownSerializer.nodes.text,
    // CodeBlockLowlight stores the lang under `language`; the default
    // serializer reads `params`.
    codeBlock: (state, node) => {
      state.write('```' + (node.attrs.language || '') + '\n');
      state.text(node.textContent, false);
      state.ensureNewLine();
      state.write('```');
      state.closeBlock(node);
    },
    hiddenBlock: (state, node) => {
      const visible = (node.attrs.visible || []).join(',');
      state.write(`[[hidden id=${node.attrs.id} visible=${visible}]]`);
      state.ensureNewLine();
      state.renderContent(node);
      state.ensureNewLine();
      state.write('[[/hidden]]');
      state.closeBlock(node);
    },
    hiddenPlaceholder: (state, node) => {
      state.write(`[[hidden id=${node.attrs.id} redacted]]`);
      state.closeBlock(node);
    },
  },
  {
    italic: defaultMarkdownSerializer.marks.em,
    bold: defaultMarkdownSerializer.marks.strong,
    code: defaultMarkdownSerializer.marks.code,
    link: defaultMarkdownSerializer.marks.link,
    strike: {
      open: '~~',
      close: '~~',
      mixable: true,
      expelEnclosingWhitespace: true,
    },
    underline: {
      open: '<u>',
      close: '</u>',
      mixable: true,
      expelEnclosingWhitespace: true,
    },
    char: {
      open: (_state, mark) =>
        mark.attrs.init ? `<char init="${mark.attrs.init}">` : '<char>',
      close: '</char>',
      mixable: false,
      expelEnclosingWhitespace: true,
    },
  },
);

export function serializeDoc(doc) {
  return tiptapMarkdownSerializer.serialize(doc);
}
